import {Component, OnInit, OnDestroy, Inject} from '@angular/core';
import { MatDialogRef, MatDialog, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {ItemSummaryOpportunityService} from '../../shared/opportunity/item-summary-opportunity.service';
import {Asset, AssetLien} from '../../shared/domain/item';
import {FormGroup} from '@angular/forms';
import {AssetLienService} from './shared/asset-lien-service';
import {AssetLienEditComponent} from './asset-lien-edit/asset-lien-edit.component';
import {Subscription} from 'rxjs';
import {Opportunity} from '../../shared/domain/opportunity';

import {UserSessionService} from '@rbaip/ui-component';
import {
  ASSET_PACKAGE_STATUS_IN_PRICING, ASSET_PACKAGE_STATUS_RESENT_FOR_PRICING,
  ASSET_PACKAGE_STATUS_SENT_FOR_PRICING,
  AssetPackageDto
} from '../../shared/domain/asset-package';
import {ItemSummaryAssetControlGroupService} from '../../shared/asset-package/item-summary-asset-control-group.service';

@Component({
  selector: 'lib-item-summary-asset-lien',
  templateUrl: './asset-lien.component.html',
  styleUrls: ['./asset-lien.component.scss']
})
export class AssetLienComponent implements OnInit , OnDestroy {
  asset: Asset;
  index = 1;
  addAnAssetLienForm: FormGroup;
  assetLiens: Array<AssetLien> = [];
  private opportunitySubscription: Subscription;
  assetControlGroup: AssetPackageDto;
  opportunity: Opportunity;
  contractId: number;
  public isTm: boolean;
  public isRm: boolean;
  hasCheckedUserRole: boolean;
  editAssetFromPricing: boolean;
  private currentUserChangedSubscription: Subscription;
  private assetControlGroupSubscription: Subscription;
  private packageTeamMemberSubscription: Subscription;
  private blanketLienDetailsSubscription: Subscription;

  constructor(private dialogRef: MatDialogRef<AssetLienComponent>,
              private dialog: MatDialog,
              private opportunityService: ItemSummaryOpportunityService,
              private assetLienService: AssetLienService,
              private assetControlGroupService: ItemSummaryAssetControlGroupService,
              private userSessionService: UserSessionService,
              @Inject(MAT_DIALOG_DATA) public data: any) {
    this.subscribeUserChanged();
    this.asset = data.asset;
    this.editAssetFromPricing = data.editAssetFromPricing;
  }

  ngOnInit() {
    this.subscribePackageInfo();
    this.subscribeAsssetLienDetails();
    this.checkPermission();
  }

  ngOnDestroy() {
    if (this.opportunitySubscription) {
      this.opportunitySubscription.unsubscribe();
    }
    if (this.currentUserChangedSubscription) {
      this.currentUserChangedSubscription.unsubscribe();
    }
    if (this.assetControlGroupSubscription) {
      this.assetControlGroupSubscription.unsubscribe();
    }
    if (this.packageTeamMemberSubscription) {
      this.packageTeamMemberSubscription.unsubscribe();
    }
    if (this.blanketLienDetailsSubscription) {
      this.blanketLienDetailsSubscription.unsubscribe();
    }
  }

  private checkPermission(): void {
    if ( this.userSessionService.getCurrentUser() ) {
      this.isCurrentUserIsPackageTeamMember(this.userSessionService.getCurrentUser().userId);
    }
  }

  private isCurrentUserIsPackageTeamMember(userId: number) {
    if (this.asset.assetControlGroupId > 0) {
      this.packageTeamMemberSubscription = this.assetControlGroupService.isPackageTeamMember(this.asset.assetControlGroupId, userId)
        .subscribe(userRoleType => {
          if (userRoleType) {
            this.isTm = userRoleType.tm;
            this.isRm = userRoleType.rm;
          }
          this.hasCheckedUserRole = true;
        });
    }
  }


  private subscribePackageInfo(): void {
    this.assetControlGroupSubscription = this.assetControlGroupService.getAssetPackageInfo(this.asset.assetControlGroupId)
      .subscribe( assetControlGroup => {
        this.assetControlGroup = AssetPackageDto.constructFromObjectJson(assetControlGroup);
        if (this.assetControlGroup && this.assetControlGroup.opportunityId) {
          this.loadOpportunityDetails();
        }
      });
  }

  private loadOpportunityDetails() {
    if (this.assetControlGroup && this.assetControlGroup.opportunityId) {
      this.opportunitySubscription = this.opportunityService.getOpportunityDetails(this.assetControlGroup.opportunityId).subscribe(
        opportunity => {
          this.opportunity = opportunity;
          this.opportunityService.selectedOpportunityChanged.next(opportunity);
          this.contractId = this.opportunity.sellerContractId;
        });
    }
  }

  private subscribeUserChanged() {
    this.currentUserChangedSubscription = this.userSessionService.userChanged
      .subscribe((siteUser) => {
        if (siteUser) {
          this.isCurrentUserIsPackageTeamMember(siteUser.userId);
        }
      });
  }

  subscribeAsssetLienDetails() {
    this.blanketLienDetailsSubscription = this.assetLienService.getBlanketLienDetails(this.asset.assetControlGroupId)
      .subscribe(
        assetLiens => {
          this.assetLiens = assetLiens;
        });
  }

  public createLienHolder() {
    const lienFields = new AssetLien();
    lienFields.index = this.assetLiens.length + 1;
    lienFields.acgId = this.asset.assetControlGroupId;
    lienFields.lienTypeId = 3;

    this.dialog.open(AssetLienEditComponent, {
      panelClass: 'mat-panel--full-height',
      autoFocus : false,
      data: {
        editing: false,
        item: lienFields
      }
    }).afterClosed().subscribe(response => {
      if (response) {
        const [assetLien, isDeleted] = response;
        if (!isDeleted) {
          this.assetLiens = assetLien;
        }
      }
    });
  }

  public editLienHolder(event) {
    this.dialog.open(AssetLienEditComponent, {
      panelClass: 'mat-panel--full-height',
      autoFocus : false,
      data: {
        editing: true,
        item: event
      }
    }).afterClosed().subscribe(response => {
      if (response) {
        const [assetLien, isDeleted] = response;
        if (isDeleted) {
          this.assetLiens = this.assetLiens.filter(lien => lien.lienId !== assetLien.lienId);
        } else {
          this.assetLiens = assetLien;
        }
      }
    });
  }

  public cancel() {
    this.dialogRef.close();
  }


  closeSuccess() {
    this.dialogRef.close(this.addAnAssetLienForm);
  }

  public canModifyLien(): boolean {
    return !(this.contractId && this.isTm) && this.hasCheckedUserRole && !!this.opportunity &&
      ( this.assetControlGroup.isMarsOperated || this.assetControlGroup.isIPWebOperated ) && !this.isPackageIsInPricing();
  }

  public isPackageIsInPricing(): boolean {
    if(this.editAssetFromPricing) return false;

    return this.assetControlGroup && !this.asset.equipId
      && (this.assetControlGroup.acgStatusId === ASSET_PACKAGE_STATUS_SENT_FOR_PRICING
        || this.assetControlGroup.acgStatusId === ASSET_PACKAGE_STATUS_IN_PRICING
        || this.assetControlGroup.acgStatusId === ASSET_PACKAGE_STATUS_RESENT_FOR_PRICING)
  }
}
